import { loadActivity, treatmentsFor } from '../registry';
import type {
  ActivityHandle,
  ActivitySpec,
  Blok,
  Jalur,
  KelasSettings,
  LearningModule,
  Peran,
  Subtema,
  Treatment,
} from '../types';
import { escapeHtml } from '../activities/scene';
import { playEffect } from './efek';
import { createFeedback } from './feedback';
import {
  clearHalaman,
  readHalaman,
  readJalur,
  readSettings,
  resolveNam,
  screensFor,
  writeJalur,
  writeSettings,
  type JalurState,
} from './kelas';
import { NARRATION_ENABLED, createNarrator } from './narrator';
import { markCompleted, readCompleted } from './progress';
import { askOnPanel, canGoFullscreen, goFullscreen } from './sheet';

/**
 * Plays one module: Mode Guru first, then the day's screens.
 *
 * Everything cross-cutting lives here rather than in the activities, because
 * the spec asks for it on *every* screen and a rule enforced once cannot be
 * forgotten by screen eleven:
 *
 *  - narration on entry, and its words always on screen as the caption the
 *    teacher reads from - every line an activity says goes through `say`
 *  - no failure state and no score
 *  - the teacher can move in either direction at any time, whatever the
 *    children have or have not finished
 *
 * **Mode Guru** is the spec's Layar 0: never seen by the children, and it has
 * to go from a dark panel to a running lesson in under thirty seconds. So it
 * opens with the last path already chosen and one big button, and everything
 * else on it is optional. The three paths are the spec's answer to "a teacher
 * who cannot shorten a lesson stops using the app" - that is a feature, and it
 * is the first thing on the screen.
 */

const PERAN_LABEL: Record<Peran, string> = {
  multi: 'Multi · 4 anak',
  satu: '1 anak',
  kelas: 'Seluruh kelas',
  guru: 'Guru',
};

const JALUR_INFO: Record<Jalur, { label: string; waktu: string; kapan: string }> = {
  inti: { label: 'Inti', waktu: '120 menit', kapan: 'Hari biasa, dengan istirahat di tengah.' },
  singkat: { label: 'Singkat', waktu: '45 menit', kapan: 'Kelas gelisah, ada acara, atau separuh anak tidak masuk.' },
  lengkap: { label: 'Lengkap', waktu: 'dua sesi', kapan: 'Subtema diperpanjang. Sesi pertama blok A–D, sesi kedua E–G.' },
};

const BLOK_DEFAULT: Record<Blok, string> = {
  A: 'Pembuka',
  B: 'Mengamati & Bahasa',
  C: 'Suara & Perasaan',
  D: 'Gerak & Tenang',
  E: 'Memilah & Merawat',
  F: 'Seni & Jati Diri',
  G: 'Penutup',
};

export async function renderPlayer(
  host: HTMLElement,
  subtema: Subtema,
  module: LearningModule,
): Promise<() => void> {
  const listeners = new AbortController();
  const signal = listeners.signal;
  const narrator = createNarrator();

  let all: ActivitySpec[] = [];
  try {
    const loaded = await module.load?.();
    all = [...(loaded?.activities ?? [])].sort((a, b) => a.layar - b.layar);
  } catch {
    all = [];
  }

  if (all.length === 0) {
    host.innerHTML = `
      <div class="mod mod--empty">
        <a class="mod__back" href="#/belajar">&larr; Kembali</a>
        <p>Modul ini belum tersedia di perangkat. Sambungkan ke internet lalu buka kembali.</p>
      </div>`;
    return () => {
      listeners.abort();
      narrator.destroy();
    };
  }

  const allLayar = all.map((a) => a.layar);
  const blokNama = { ...BLOK_DEFAULT, ...module.blokNama };
  let settings: KelasSettings = readSettings();
  let jalur: JalurState = readJalur(module, allLayar);
  const done = readCompleted(module.id);

  /** The screens switched on for today, in order. */
  let activities: ActivitySpec[] = [];
  let index = 0;
  let treatment: Treatment = 'flat';
  let handle: ActivityHandle | null = null;
  let mountToken = 0;
  let mode: 'guru' | 'main' = 'guru';

  host.innerHTML = `
    <div class="mod" data-hewan="${escapeHtml(module.hewan ?? '')}">
      <section class="guru" hidden></section>

      <div class="mod__play" hidden>
        <header class="mod__bar">
          <button class="mod__back" type="button">&larr; Mode Guru</button>
          <div class="mod__titles">
            <span class="mod__module"></span>
            <span class="mod__screen"></span>
          </div>
          <div class="mod__badges"></div>
          <button class="mod__teacher" type="button" aria-expanded="false">Catatan guru</button>
          <div class="mod__nav">
            <button class="mod__prev" type="button" aria-label="Layar sebelumnya">&larr;</button>
            <button class="mod__next" type="button">Berikutnya &rarr;</button>
          </div>
        </header>

        <nav class="mod__steps" aria-label="Layar hari ini">
          <div class="mod__dots"></div>
          <div class="mod__treatments" role="radiogroup" aria-label="Cara tampilan"></div>
        </nav>

        <section class="mod__teacher-note" hidden></section>

        <div class="mod__caption">
          <button class="mod__replay" type="button" aria-label="Ulangi narasi">&#9654;</button>
          <p class="mod__narration" aria-live="polite"></p>
        </div>

        <main class="mod__stage"></main>
      </div>
    </div>
  `;

  const el = <T extends Element>(selector: string) => host.querySelector(selector) as T;

  const guru = el<HTMLElement>('.guru');
  const play = el<HTMLElement>('.mod__play');
  const stage = el<HTMLElement>('.mod__stage');
  const steps = el<HTMLElement>('.mod__dots');
  const caption = el<HTMLElement>('.mod__narration');
  const badges = el<HTMLElement>('.mod__badges');
  const teacherNote = el<HTMLElement>('.mod__teacher-note');
  const teacherToggle = el<HTMLButtonElement>('.mod__teacher');
  const treatments = el<HTMLElement>('.mod__treatments');
  const prev = el<HTMLButtonElement>('.mod__prev');
  const next = el<HTMLButtonElement>('.mod__next');
  const moduleLine = el<HTMLElement>('.mod__module');

  // Created once over the stage and outliving each activity, so a celebration
  // on the last tap of a screen is not cut off by that screen unmounting.
  const feedback = createFeedback(stage);

  /* --- Caption: the line the teacher reads -------------------------------- */

  let captionTimer = 0;
  function say(text: string): void {
    const line = resolveNam(text, settings);
    caption.textContent = line;
    // A new line arriving mid-screen is marked, because with narration off the
    // teacher is the voice and needs to notice there is something new to read.
    caption.classList.remove('is-new');
    void caption.offsetWidth;
    caption.classList.add('is-new');
    window.clearTimeout(captionTimer);
    captionTimer = window.setTimeout(() => caption.classList.remove('is-new'), 1400);
    narrator.speak(line);
  }

  /* --- Mode Guru ------------------------------------------------------------ */

  function blocks(): Blok[] {
    return [...new Set(all.map((a) => a.blok))];
  }

  function renderGuru(): void {
    const halaman = readHalaman();
    const perHewan = ['kucing', 'anjing', 'kelinci']
      .map((h) => ({ h, n: halaman.filter((m) => m.hewan === h).length }))
      .filter((x) => x.n > 0)
      .map((x) => `${x.n} ${x.h}`)
      .join(', ');
    const resumeAt = all.findIndex((a) => jalur.layar.includes(a.layar) && !done.has(a.id));

    guru.innerHTML = `
      <header class="guru__head">
        <a class="guru__exit" href="#/belajar">&larr; Semua hari</a>
        <div>
          <p class="guru__eyebrow">Mode Guru &middot; tidak dilihat anak</p>
          <h1>Hari ${module.hari} — ${escapeHtml(module.title)}</h1>
          ${module.nilai ? `<p class="guru__nilai">Nilai hari ini: <strong>${escapeHtml(module.nilai)}</strong></p>` : ''}
        </div>
        ${canGoFullscreen() ? '<button type="button" class="guru__fullscreen">Layar penuh</button>' : ''}
      </header>

      <section class="guru__section">
        <h2>Pilih jalur</h2>
        <div class="guru__jalur" role="radiogroup" aria-label="Jalur">
          ${(['inti', 'singkat', 'lengkap'] as Jalur[])
            .map((j) => {
              const n = screensFor(module, j, allLayar).length;
              return `
              <button type="button" role="radio" class="guru__path" data-jalur="${j}"
                aria-checked="${jalur.jalur === j}">
                <strong>${JALUR_INFO[j].label}</strong>
                <span>${n} layar &middot; ${JALUR_INFO[j].waktu}</span>
                <small>${JALUR_INFO[j].kapan}</small>
              </button>`;
            })
            .join('')}
        </div>
      </section>

      <section class="guru__section">
        <h2>Layar yang aktif hari ini <small>${jalur.layar.length} dari ${all.length}</small></h2>
        <div class="guru__blocks">
          ${blocks()
            .map(
              (b) => `
            <div class="guru__block">
              <div class="guru__block-name"><span>${b}</span>${escapeHtml(blokNama[b])}</div>
              <div class="guru__screens">
                ${all
                  .filter((a) => a.blok === b)
                  .map((a) => {
                    const on = jalur.layar.includes(a.layar);
                    const inti = module.jalur?.inti.includes(a.layar);
                    return `
                    <button type="button" class="guru__screen guru__screen--${a.peran}" data-layar="${a.layar}"
                      aria-pressed="${on}">
                      <span class="guru__no">${a.layar}</span>
                      <span class="guru__screen-title">${escapeHtml(a.title)}</span>
                      <span class="guru__screen-meta">${PERAN_LABEL[a.peran]}${a.menit ? ` &middot; ±${a.menit} mnt` : ''}${inti ? ' &middot; <b>inti</b>' : ''}</span>
                      ${done.has(a.id) ? '<span class="guru__done" aria-label="sudah">&#10003;</span>' : ''}
                    </button>`;
                  })
                  .join('')}
              </div>
            </div>`,
            )
            .join('')}
        </div>
      </section>

      <section class="guru__section guru__row">
        <div>
          <h2>Anak di papan</h2>
          <p class="guru__hint">Untuk layar Multi. Spesifikasi dirancang untuk empat.</p>
          <div class="guru__seg" role="radiogroup" aria-label="Anak di papan">
            ${[1, 2, 3, 4]
              .map(
                (n) =>
                  `<button type="button" role="radio" data-anak="${n}" aria-checked="${settings.anak === n}">${n}</button>`,
              )
              .join('')}
          </div>
        </div>
        <div>
          <h2>Versi narasi NAM</h2>
          <p class="guru__hint">Mengubah baris narasi bernuansa agama.</p>
          <div class="guru__seg" role="radiogroup" aria-label="Versi narasi">
            ${(['Tuhan', 'Allah'] as const)
              .map(
                (v) =>
                  `<button type="button" role="radio" data-nam="${v}" aria-checked="${settings.nam === v}">${v}</button>`,
              )
              .join('')}
          </div>
        </div>
        <div>
          <h2>Halaman Kita</h2>
          <p class="guru__hint">${halaman.length ? `${halaman.length} binatang: ${escapeHtml(perHewan)}.` : 'Masih kosong. Terisi dari layar Warnai.'}</p>
          <button type="button" class="guru__clear" ${halaman.length ? '' : 'disabled'}>Kosongkan halaman&hellip;</button>
        </div>
      </section>

      <footer class="guru__foot">
        <button type="button" class="guru__start" ${jalur.layar.length ? '' : 'disabled'}>
          Mulai pelajaran &rarr;
        </button>
        ${
          resumeAt > 0
            ? `<button type="button" class="guru__resume" data-at="${all[resumeAt]!.layar}">
                 Lanjutkan dari Layar ${all[resumeAt]!.layar}: ${escapeHtml(all[resumeAt]!.title)}
               </button>`
            : ''
        }
      </footer>
    `;
  }

  function saveJalur(): void {
    writeJalur(module.id, jalur);
    renderGuru();
  }

  guru.addEventListener(
    'click',
    (event) => {
      const t = event.target as HTMLElement;
      const path = t.closest<HTMLElement>('[data-jalur]');
      if (path) {
        const j = path.dataset['jalur'] as Jalur;
        jalur = { jalur: j, layar: screensFor(module, j, allLayar) };
        saveJalur();
        return;
      }
      const screen = t.closest<HTMLElement>('[data-layar]');
      if (screen) {
        const n = Number(screen.dataset['layar']);
        const on = jalur.layar.includes(n);
        jalur = {
          ...jalur,
          layar: on ? jalur.layar.filter((x) => x !== n) : [...jalur.layar, n].sort((a, b) => a - b),
        };
        saveJalur();
        return;
      }
      const anak = t.closest<HTMLElement>('[data-anak]');
      if (anak) {
        settings = { ...settings, anak: Number(anak.dataset['anak']) };
        writeSettings(settings);
        renderGuru();
        return;
      }
      const nam = t.closest<HTMLElement>('[data-nam]');
      if (nam) {
        settings = { ...settings, nam: nam.dataset['nam'] === 'Allah' ? 'Allah' : 'Tuhan' };
        writeSettings(settings);
        renderGuru();
        return;
      }
      if (t.closest('.guru__clear')) {
        // The only destructive control in the lane, so it asks. A week of
        // children's drawings is not something to lose to a stray tap.
        // Inside .mod, so it wears the day's colours.
        void askOnPanel(host.querySelector<HTMLElement>('.mod') ?? host, {
          title: 'Kosongkan Halaman Kita?',
          body: 'Semua binatang yang sudah diwarnai dan dinamai kelas ini akan hilang.',
          confirm: 'Ya, kosongkan',
          cancel: 'Jangan',
          destructive: true,
        }).then((yes) => {
          if (!yes) return;
          clearHalaman();
          renderGuru();
        });
        return;
      }
      if (t.closest('.guru__fullscreen')) {
        goFullscreen();
        return;
      }
      if (t.closest('.guru__start')) {
        startLesson();
        return;
      }
      const resume = t.closest<HTMLElement>('.guru__resume');
      if (resume) startLesson(Number(resume.dataset['at']));
    },
    { signal },
  );

  function showGuru(): void {
    handle?.unmount();
    handle = null;
    narrator.stop();
    mode = 'guru';
    play.hidden = true;
    guru.hidden = false;
    renderGuru();
    guru.querySelector<HTMLElement>('.guru__start')?.focus();
  }

  /** Starts at a screen number, or at the first switched-on screen. */
  function startLesson(atLayar?: number): void {
    activities = all.filter((a) => jalur.layar.includes(a.layar));
    if (activities.length === 0) return;
    mode = 'main';
    guru.hidden = true;
    play.hidden = false;
    void show(Math.max(0, activities.findIndex((a) => a.layar === atLayar)));
  }

  /* --- The lesson ------------------------------------------------------------ */

  const TREATMENT_LABELS: Record<Treatment, string> = {
    flat: 'Datar',
    parallax: 'Berlapis',
    depth: '3D',
  };

  function renderTreatments(spec: ActivitySpec): void {
    const available = treatmentsFor(spec.kind);
    treatments.hidden = available.length < 2;
    treatments.innerHTML = treatments.hidden
      ? ''
      : available
          .map(
            (t) => `<button class="mod__treatment" type="button" role="radio"
                  aria-checked="${t === treatment}" data-treatment="${t}">${TREATMENT_LABELS[t]}</button>`,
          )
          .join('');
  }

  function renderSteps(): void {
    const breakAfter = jalur.jalur === 'inti' ? module.istirahatSetelah : undefined;
    let lastBlok: Blok | null = null;
    steps.innerHTML = activities
      .map((a, i) => {
        const state = i === index ? 'current' : done.has(a.id) ? 'done' : 'todo';
        // No `title` tooltips anywhere here: a panel has no hover, so a name that
        // only appears on hover is a name nobody sees. The block's name is in
        // the header instead, beside the screen it belongs to.
        const blokLabel =
          a.blok !== lastBlok ? `<span class="mod__blok" aria-hidden="true">${a.blok}</span>` : '';
        lastBlok = a.blok;
        const rest = breakAfter === a.layar ? '<span class="mod__rest">istirahat 15 mnt</span>' : '';
        return `${blokLabel}<button class="mod__step mod__step--${state} mod__step--${a.peran}" type="button"
                  data-index="${i}" aria-label="Layar ${a.layar}: ${escapeHtml(a.title)}"${i === index ? ' aria-current="step"' : ''}>
                  <span>${a.layar}</span>
                </button>${rest}`;
      })
      .join('');
    steps.querySelector('[aria-current]')?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }

  function renderNote(spec: ActivitySpec): void {
    teacherNote.innerHTML = `
      <div class="mod__note-screen">
        <p class="mod__note-eyebrow">Layar ${spec.layar} &middot; Blok ${spec.blok}, ${escapeHtml(blokNama[spec.blok])}</p>
        <h3>${escapeHtml(spec.title)}</h3>
        <dl>
          <dt>Tujuan</dt><dd>${escapeHtml(spec.fungsi)}</dd>
          ${spec.dinilai ? `<dt>Yang dinilai</dt><dd>${escapeHtml(spec.dinilai)}</dd>` : ''}
          <dt>Elemen CP</dt><dd>${escapeHtml(spec.elemen)}</dd>
          <dt>Siapa</dt><dd>${PERAN_LABEL[spec.peran]}${spec.menit ? ` &middot; ±${spec.menit} menit` : ''}</dd>
        </dl>
      </div>
      <div class="mod__note-day">
        <p class="mod__note-eyebrow">${escapeHtml(subtema.tema)} &middot; ${escapeHtml(subtema.subtema)} &middot; ${escapeHtml(subtema.jenjang)}</p>
        <dl>${module.tujuan
          .map((t) => `<dt>${escapeHtml(t.dimensi)}</dt><dd>${escapeHtml(t.tujuan)}</dd>`)
          .join('')}</dl>
      </div>`;
  }

  async function show(nextIndex: number): Promise<void> {
    const token = ++mountToken;

    index = Math.max(0, Math.min(activities.length - 1, nextIndex));
    const spec = activities[index]!;

    handle?.unmount();
    handle = null;
    narrator.stop();

    el<HTMLElement>('.mod__screen').textContent = `${spec.layar}. ${spec.title}`;
    moduleLine.textContent = `Hari ${module.hari} · Blok ${spec.blok}, ${blokNama[spec.blok]} · ${index + 1} dari ${activities.length}`;
    badges.innerHTML = `
      <span class="tag tag--${spec.peran}">${PERAN_LABEL[spec.peran]}</span>
      ${module.jalur?.inti.includes(spec.layar) ? '<span class="tag tag--inti">Jalur inti</span>' : ''}`;
    caption.textContent = resolveNam(spec.narration, settings);
    prev.disabled = index === 0;
    next.textContent = index === activities.length - 1 ? 'Selesai' : 'Berikutnya →';
    renderSteps();
    renderNote(spec);

    if (!treatmentsFor(spec.kind).includes(treatment)) treatment = 'flat';
    renderTreatments(spec);

    stage.dataset['kind'] = spec.kind;
    stage.dataset['peran'] = spec.peran;
    stage.dataset['treatment'] = treatment;

    // A screen that takes more than a moment to arrive (a first visit, a slow
    // panel) shows Tunas waiting rather than an empty stage, which in front of
    // a class reads as "it broke". Shown late so a fast screen never flickers.
    stage.querySelectorAll(':scope > .mod__loading').forEach((n) => n.remove());
    const loadingTimer = window.setTimeout(() => {
      if (token !== mountToken) return;
      stage.insertAdjacentHTML(
        'beforeend',
        '<div class="mod__loading" role="status"><span></span><span></span><span></span><p>Sebentar ya&hellip;</p></div>',
      );
    }, 350);
    const loaded = (): void => {
      window.clearTimeout(loadingTimer);
      stage.querySelectorAll(':scope > .mod__loading').forEach((n) => n.remove());
    };

    const module_ = await loadActivity(spec.kind, treatment);
    if (token !== mountToken) {
      window.clearTimeout(loadingTimer);
      return;
    }

    if (!module_) {
      loaded();
      stage.innerHTML = `<p class="mod__missing">Jenis kegiatan "${escapeHtml(spec.kind)}" belum tersedia.</p>`;
      return;
    }

    // Leftovers from a screen whose mount was overtaken.
    stage.querySelectorAll(':scope > :not(.mod-feedback):not(.mod__loading)').forEach((n) => n.remove());

    // Spoken before the screen mounts, so a line the screen says straight away
    // (its first question) follows the opening instead of being cut off by it.
    narrator.speak(resolveNam(spec.narration, settings));

    handle = await module_.mount(stage, {
      spec,
      moduleId: module.id,
      module,
      settings,
      narrator,
      feedback,
      say,
      sound: (id, options) => void playEffect(id, options),
      complete() {
        done.add(spec.id);
        markCompleted(module.id, spec.id);
        renderSteps();
        next.classList.add('is-ready');
      },
    });

    if (token !== mountToken) {
      window.clearTimeout(loadingTimer);
      handle.unmount();
      handle = null;
      return;
    }

    loaded();
    next.classList.toggle('is-ready', done.has(spec.id));
  }

  prev.addEventListener('click', () => void show(index - 1), { signal });

  next.addEventListener(
    'click',
    () => {
      if (index === activities.length - 1) {
        showGuru();
        return;
      }
      void show(index + 1);
    },
    { signal },
  );

  steps.addEventListener(
    'click',
    (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>('[data-index]');
      if (button) void show(Number(button.dataset['index']));
    },
    { signal },
  );

  treatments.addEventListener(
    'click',
    (event) => {
      const button = (event.target as HTMLElement).closest<HTMLElement>('[data-treatment]');
      const next_ = button?.dataset['treatment'] as Treatment | undefined;
      if (!next_ || next_ === treatment) return;
      treatment = next_;
      void show(index);
    },
    { signal },
  );

  el<HTMLButtonElement>('.mod__replay').hidden = !NARRATION_ENABLED;
  el<HTMLButtonElement>('.mod__replay').addEventListener(
    'click',
    () => narrator.speak(caption.textContent ?? ''),
    { signal },
  );

  el<HTMLButtonElement>('.mod__back').addEventListener('click', showGuru, { signal });

  teacherToggle.addEventListener(
    'click',
    () => {
      const open = teacherNote.hidden;
      teacherNote.hidden = !open;
      teacherToggle.setAttribute('aria-expanded', String(open));
    },
    { signal },
  );

  // A long press on a touch panel opens the browser's menu (copy, save image,
  // open in new tab) - and a child resting a finger on a card is a long press.
  host.addEventListener('contextmenu', (event) => event.preventDefault(), { signal });

  document.addEventListener(
    'fullscreenchange',
    () => {
      if (mode === 'guru') renderGuru();
    },
    { signal },
  );

  // The panel has no keyboard, so nothing depends on this. It is here for a
  // presentation remote, which sends Page Up/Down, if a teacher brings one.
  window.addEventListener(
    'keydown',
    (event) => {
      if (mode !== 'main') return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, [contenteditable]')) return;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') {
        event.preventDefault();
        next.click();
      } else if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        if (!prev.disabled) void show(index - 1);
      }
    },
    { signal },
  );

  showGuru();

  return () => {
    listeners.abort();
    window.clearTimeout(captionTimer);
    handle?.unmount();
    feedback.destroy();
    narrator.destroy();
  };
}
